// ABOUTME: Pathless layout for chat routes (/ and /chat/$conversationId).
// ABOUTME: Handles PDF/Video side panels and bidirectional URL↔chat state sync.

import CSVViewer from "@/components/CSVViewer";
import ChatContainer from "@/components/ChatContainer";
import DatasetSourcePanel from "@/components/DatasetSourcePanel";
import DocumentViewer from "@/components/DocumentViewer";
import PDFViewer from "@/components/PDFViewer";
import { formatTimestamp } from "@/components/VideoPlayer";
import type { VideoClip } from "@/components/VideoPlayer";
import { useChat } from "@/contexts/useChat";
import type { DatasetSource } from "@/lib/sources";
import {
  Outlet,
  createFileRoute,
  useLocation,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

const VideoPlayer = lazy(() => import("@/components/VideoPlayer"));

export const Route = createFileRoute("/_authenticated/_chat")({
  component: ChatLayout,
});

function ChatLayout() {
  const [showPdf, setShowPdf] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string | null>(null);
  const [pdfTitle, setPdfTitle] = useState<string>("");
  const [pdfPage, setPdfPage] = useState<number | undefined>(undefined);

  // Video side panel state
  const [showVideo, setShowVideo] = useState(false);
  const [videoClips, setVideoClips] = useState<VideoClip[]>([]);

  // Document side panel state
  const [showDoc, setShowDoc] = useState(false);
  const [docId, setDocId] = useState<string | null>(null);
  const [docTitle, setDocTitle] = useState<string>("");

  // CSV side panel state
  const [showCsv, setShowCsv] = useState(false);
  const [currentCsvUrl, setCurrentCsvUrl] = useState<string | null>(null);
  const [csvTitle, setCsvTitle] = useState<string>("");

  // Dataset side panel state (events table, form submission records)
  const [showData, setShowData] = useState(false);
  const [currentDataset, setCurrentDataset] = useState<DatasetSource | null>(
    null,
  );
  const [datasetAnchorRowId, setDatasetAnchorRowId] = useState<
    string | undefined
  >(undefined);
  const [datasetFullscreen, setDatasetFullscreen] = useState(false);

  const openPdf = (url: string, title: string, page?: number) => {
    setCurrentPdfUrl(url);
    setPdfTitle(title);
    setPdfPage(page);
    setShowPdf(true);
    // Close video, doc, CSV, and data panels when opening PDF
    setShowVideo(false);
    setShowDoc(false);
    setShowCsv(false);
    setShowData(false);
  };

  const closePdf = useCallback(() => {
    setShowPdf(false);
    setCurrentPdfUrl(null);
    setPdfTitle("");
    setPdfPage(undefined);
  }, []);

  const openVideo = useCallback((clips: VideoClip[], _activeIndex: number) => {
    setVideoClips(clips);
    setShowVideo(true);
    // Close PDF, doc, CSV, and data panels when opening video
    setShowPdf(false);
    setCurrentPdfUrl(null);
    setPdfTitle("");
    setPdfPage(undefined);
    setShowDoc(false);
    setShowCsv(false);
    setShowData(false);
  }, []);

  const closeVideo = useCallback(() => {
    setShowVideo(false);
    setVideoClips([]);
  }, []);

  const openDoc = useCallback((documentId: string, title: string) => {
    setDocId(documentId);
    setDocTitle(title);
    setShowDoc(true);
    // Close PDF, video, CSV, and data panels when opening doc
    setShowPdf(false);
    setCurrentPdfUrl(null);
    setPdfTitle("");
    setPdfPage(undefined);
    setShowVideo(false);
    setVideoClips([]);
    setShowCsv(false);
    setShowData(false);
  }, []);

  const closeDoc = useCallback(() => {
    setShowDoc(false);
    setDocId(null);
    setDocTitle("");
  }, []);

  const openCsv = useCallback((url: string, title: string) => {
    setCurrentCsvUrl(url);
    setCsvTitle(title);
    setShowCsv(true);
    // Close PDF, video, doc, and data panels when opening CSV
    setShowPdf(false);
    setCurrentPdfUrl(null);
    setPdfTitle("");
    setPdfPage(undefined);
    setShowVideo(false);
    setVideoClips([]);
    setShowDoc(false);
    setShowData(false);
  }, []);

  const closeCsv = useCallback(() => {
    setShowCsv(false);
    setCurrentCsvUrl(null);
    setCsvTitle("");
  }, []);

  const openDataset = useCallback((source: DatasetSource, rowId?: string) => {
    setCurrentDataset(source);
    setDatasetAnchorRowId(rowId);
    setShowData(true);
    // Close all other panels when opening the dataset panel
    setShowPdf(false);
    setCurrentPdfUrl(null);
    setPdfTitle("");
    setPdfPage(undefined);
    setShowVideo(false);
    setVideoClips([]);
    setShowDoc(false);
    setShowCsv(false);
  }, []);

  const closeDataset = useCallback(() => {
    setShowData(false);
    setCurrentDataset(null);
    setDatasetAnchorRowId(undefined);
    setDatasetFullscreen(false);
  }, []);

  const toggleDatasetFullscreen = useCallback(() => {
    setDatasetFullscreen((prev) => !prev);
  }, []);

  // URL-based routing
  const location = useLocation();
  const navigate = useNavigate();
  const { conversationId: urlConversationId } = useParams({
    strict: false,
  }) as { conversationId?: string };
  const { currentConversationId, loadConversation, createNewConversation } =
    useChat();

  // Track currentConversationId via ref to avoid stale closures
  const currentConvIdRef = useRef(currentConversationId);
  currentConvIdRef.current = currentConversationId;

  // URL → Chat state sync
  useEffect(() => {
    if (urlConversationId) {
      if (urlConversationId !== currentConvIdRef.current) {
        // Await load so closePdf and new messages arrive in the same render
        loadConversation(urlConversationId).then((loaded) => {
          if (!loaded) {
            navigate({ to: "/" });
            return;
          }

          closePdf();
          closeVideo();
          closeDoc();
          closeCsv();
          closeDataset();
        });
      }
    } else if (
      location.pathname === "/" ||
      location.pathname.startsWith("/machine/")
    ) {
      if (currentConvIdRef.current) {
        createNewConversation();
      }
      closePdf();
      closeVideo();
      closeDoc();
      closeCsv();
      closeDataset();
    }
  }, [
    urlConversationId,
    location.pathname,
    loadConversation,
    createNewConversation,
    navigate,
    closePdf,
    closeVideo,
    closeDoc,
    closeCsv,
    closeDataset,
  ]);

  // Chat state → URL sync (new conversation gets ID from server)
  // Only navigate when currentConversationId actually changed, not when
  // the URL changed (e.g. back button). Otherwise the stale conversation ID
  // from the current render would push us forward before the state update
  // from createNewConversation() takes effect.
  const prevConvIdForSyncRef = useRef<string | null>(currentConversationId);
  useEffect(() => {
    const convIdChanged =
      currentConversationId !== prevConvIdForSyncRef.current;
    prevConvIdForSyncRef.current = currentConversationId;

    if (currentConversationId && convIdChanged && !urlConversationId) {
      const isOnChat =
        location.pathname === "/" ||
        location.pathname.startsWith("/chat") ||
        location.pathname.startsWith("/machine/");
      if (isOnChat) {
        navigate({
          to: "/chat/$conversationId",
          params: { conversationId: currentConversationId },
        });
      }
    }
  }, [currentConversationId, urlConversationId, location.pathname, navigate]);

  return (
    <>
      {/* Chat Container */}
      <div
        className={`min-w-0 overflow-hidden ${showPdf || showVideo || showDoc || showCsv || showData ? "w-1/2 shrink-0" : "w-full flex-1"}`}
      >
        <ChatContainer
          onOpenPdf={openPdf}
          onOpenVideo={openVideo}
          onOpenDoc={openDoc}
          onOpenCsv={openCsv}
          onOpenDataset={openDataset}
        />
      </div>

      {/* PDF Viewer Panel (for citations) */}
      {showPdf && currentPdfUrl && (
        <div className="min-w-0 w-1/2 shrink-0 border-l border-border">
          <PDFViewer
            key={currentPdfUrl}
            url={currentPdfUrl}
            title={pdfTitle}
            page={pdfPage}
            onClose={closePdf}
          />
        </div>
      )}

      {/* Video Side Panel (for video citations) */}
      {showVideo && videoClips.length > 0 && (
        <div className="flex h-full min-w-0 w-1/2 shrink-0 flex-col border-l border-border">
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                Loading video player...
              </div>
            }
          >
            <VideoPlayer
              key={`${videoClips[0].source.documentId}-${videoClips[0].source.chunkId}`}
              url={videoClips[0].urls.videoUrl}
              hlsUrl={videoClips[0].urls.hlsUrl}
              chaptersVttUrl={videoClips[0].urls.chaptersVttUrl}
              captionsVttUrl={videoClips[0].urls.captionsVttUrl}
              startTime={videoClips[0].source.video_start_time}
              title={`[${videoClips[0].citationNumber}] ${formatTimestamp(videoClips[0])} · ${videoClips[0].source.documentFilename}`}
              onClose={closeVideo}
            />
          </Suspense>
        </div>
      )}

      {/* Document Viewer Panel (for Tendrel doc citations) */}
      {showDoc && docId && (
        <div className="min-w-0 w-1/2 shrink-0 border-l border-border">
          <DocumentViewer
            key={docId}
            documentId={docId}
            title={docTitle}
            onClose={closeDoc}
          />
        </div>
      )}

      {/* CSV Viewer Panel (for CSV document citations) */}
      {showCsv && currentCsvUrl && (
        <div className="min-w-0 w-1/2 shrink-0 border-l border-border">
          <CSVViewer
            key={currentCsvUrl}
            url={currentCsvUrl}
            title={csvTitle}
            onClose={closeCsv}
          />
        </div>
      )}

      {/* Dataset Panel (for events / form submission citations) */}
      {showData && currentDataset && (
        <div
          className={
            datasetFullscreen
              ? "fixed inset-0 z-50 bg-background"
              : "min-w-0 w-1/2 shrink-0 border-l border-border"
          }
        >
          <DatasetSourcePanel
            key={`${currentDataset.datasetId}-${datasetAnchorRowId ?? "list"}`}
            source={currentDataset}
            anchorRowId={datasetAnchorRowId}
            isFullscreen={datasetFullscreen}
            onToggleFullscreen={toggleDatasetFullscreen}
            onClose={closeDataset}
          />
        </div>
      )}

      <Outlet />
    </>
  );
}
